import { AlertTriangle, Loader2, RefreshCw } from "lucide-react";
import { useState } from "react";

import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useM3RigeneraDocumento, useM3RigeneraPacchetto } from "@/hooks/useM3";
import { ApiError } from "@/lib/api";

/**
 * Dialogo di rigenerazione (SPEC §5). Pacchetto: due opzioni «mantieni override
 * coerenti» / «riparti da zero». Documento: mantiene di default; se un override
 * non è più coerente con la NDP (409) chiede conferma «rigenera comunque».
 */
export function RigeneraDialog({
  scope,
  caricoId,
  tipo,
  label,
  onClose,
  onDone,
}: {
  scope: "pacchetto" | "documento";
  caricoId: string;
  tipo?: string;
  label?: string;
  onClose: () => void;
  onDone: () => void;
}) {
  const rigPacchetto = useM3RigeneraPacchetto(caricoId);
  const rigDocumento = useM3RigeneraDocumento(caricoId, tipo ?? "");
  const [conflitto, setConflitto] = useState<string[] | null>(null);

  const pending = rigPacchetto.isPending || rigDocumento.isPending;

  function chiudi() {
    if (!pending) onClose();
  }

  function rigeneraPacchetto(modalita: "mantieni" | "zero") {
    rigPacchetto.mutate(
      { modalita },
      {
        onSuccess: () => {
          onDone();
          onClose();
        },
      },
    );
  }

  function rigeneraDocumento(forza: boolean) {
    rigDocumento.mutate(
      { forza },
      {
        onSuccess: () => {
          onDone();
          onClose();
        },
        onError: (err) => {
          const body = err instanceof ApiError ? (err.body as { detail?: { chiavi?: string[] } }) : null;
          if (err instanceof ApiError && err.status === 409) {
            setConflitto(body?.detail?.chiavi ?? []);
          }
        },
      },
    );
  }

  return (
    <Dialog open onOpenChange={(o) => !o && chiudi()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <RefreshCw className="h-4 w-4 text-brand-700" />
            {scope === "pacchetto" ? "Rigenera pacchetto" : `Rigenera «${label ?? tipo}»`}
          </DialogTitle>
        </DialogHeader>

        {scope === "pacchetto" ? (
          <div className="space-y-2 text-sm text-muted-foreground">
            <p>
              Il pacchetto verrà rigenerato con l'ultima versione dei template. Come
              trattare i dati inseriti a mano (n. fattura, n. certificato, ecc.)?
            </p>
            <button
              type="button"
              disabled={pending}
              onClick={() => rigeneraPacchetto("mantieni")}
              className="w-full rounded-md border border-border p-3 text-left hover:border-brand-500 hover:bg-brand-50/40"
            >
              <div className="text-[13px] font-semibold text-foreground">
                Mantieni i dati inseriti
              </div>
              <div className="text-[11.5px]">
                Riapplica gli override ancora coerenti con la NDP attuale; quelli legati
                a entità non più presenti vengono scartati.
              </div>
            </button>
            <button
              type="button"
              disabled={pending}
              onClick={() => rigeneraPacchetto("zero")}
              className="w-full rounded-md border border-border p-3 text-left hover:border-brand-500 hover:bg-brand-50/40"
            >
              <div className="text-[13px] font-semibold text-foreground">
                Riparti da zero
              </div>
              <div className="text-[11.5px]">
                Scarta tutti i dati inseriti a mano e rigenera solo dalle sorgenti.
              </div>
            </button>
            {pending && (
              <div className="flex items-center gap-2 text-[12px]">
                <Loader2 className="h-3.5 w-3.5 animate-spin" /> Rigenerazione in corso…
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-3 text-sm text-muted-foreground">
            {conflitto ? (
              <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-[12.5px] text-amber-800">
                <AlertTriangle className="mt-0.5 h-4 w-4 flex-none" />
                <div>
                  Alcuni dati inseriti a mano non sono più coerenti con la NDP e
                  verranno scartati
                  {conflitto.length ? `: ${conflitto.join(", ")}` : ""}. Rigenerare
                  comunque?
                </div>
              </div>
            ) : (
              <p>
                Rigenera il documento con l'ultima versione del suo template, mantenendo
                i campi inseriti a mano.
              </p>
            )}
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={chiudi} disabled={pending}>
                Annulla
              </Button>
              <Button onClick={() => rigeneraDocumento(conflitto != null)} disabled={pending}>
                {pending && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
                {conflitto ? "Rigenera comunque" : "Rigenera"}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
